<template>
  <div
    id="vendor-assessment-review-question-container"
    class="tw-h-full tw-max-h-fit tw-min-h-fit xl:tw-px-[60px] xl:tw-py-[55px] tw-p-[20px] tw-flex tw-flex-col tw-gap-5 tw-rounded-b-[6px] tw-border-0 tw-border-t tw-border-solid tw-border-[#EFF0F1]"
  >
    <AssessmentCategoryHeader
      :title="
        getComponentUiText(
          'app-assess-recommendations-section-assessment-questions-1',
        )
      "
      :class-name="'tw-text-[20px]'"
    />

    <div
      class="tw-flex md:tw-flex-row tw-flex-col tw-justify-between md:tw-items-center tw-flex-wrap tw-gap-5"
    >
      <!-- Search Box -->
      <VendorAssessmentSearchBox
        :class="'md:tw-max-w-[450px] tw-w-full'"
        :initial-value-search="search"
        :hint-text="getComponentUiText('app-table-search-prompt-text-1')"
        :id-search-box-wrap="`vendo-assessment-review-search-box`"
        :id-input-search="`vendo-assessment-review-search-input`"
        @update:model-value="search = $event"
      />

      <div
        id="vendo-assessment-review-action"
        class="tw-flex tw-items-center tw-flex-wrap xl:tw-gap-5 tw-gap-[20px]"
      >
        <div @click="handleShowBulkAction">
          <ButtonBulkActions
            :id-popover="'popover-btn-bulk-action-vendor-assessment'"
            :active="selectedQuestions.length >= 1"
            :show-more-actions="isShowMoreActions"
            :selected-number="selectedQuestions.length"
            :show-acction-click="true"
            :is-margin-right="false"
          >
            <CustomDropDownItem
              :style="'normal'"
              :id-text="'app-component-vendor-input-bulk-action-review-as-acceppted-1'"
              @click="showModalEditQuestion(CLR_REVIEW_OPTION.accepted)"
            />
            <CustomDropDownItem
              :style="'normal'"
              :id-text="'app-component-vendor-input-bulk-action-review-as-not-acceppted-1'"
              @click="showModalEditQuestion(CLR_REVIEW_OPTION.notAccepted)"
            />
            <CustomDropDownItem
              :style="'normal'"
              :id-text="'app-component-vendor-input-bulk-action-reset-reviewed-1'"
              @click="showModalEditQuestion(CLR_REVIEW_OPTION.resetReview)"
            />
          </ButtonBulkActions>
        </div>
        <div class="filter-action">
          <ButtonFilter
            :active="listQuestion.length || Object.keys(filterSelected).length"
            :is-filter="Object.keys(filterSelected).length > 0"
            :show-more-actions="isShowFilterAction"
            :id-popover="'popoverBtnFilterAssessment'"
            :is-margin-right="false"
            @click-away="outsideFilterModal = true"
            @click="handleShowFilterAction"
          >
            <FilterModal
              :targets="fiterTargetOption"
              :is-date="false"
              :is-search="false"
              :show-more-actions="isShowFilterAction"
              :filter-selected="filterSelected"
              :original-data="{ filterSelected: cloneDeep(originSelected) }"
              :outside-filter-modal="outsideFilterModal"
              @update:apply-filter="handleApplyFilter"
            />
          </ButtonFilter>
        </div>
      </div>
    </div>

    <div
      v-if="!isLoading"
      class="tw-flex tw-flex-col tw-gap-[20px] tw-w-full tw-rounded-[6px]"
    >
      <VendorAssessmentReviewQuestionDetail
        v-for="item in listQuestion"
        :key="item.checklistItemDateUpdated"
        :assessment-question="item"
        :reset-all-selected-questions="resetAllSelectedQuestions"
        :heading-id="`vendor-assessment-review-question-detail-heading-${
          Math.random() * 1000
        }`"
        @update-selected="handleUpdateSelected"
      />
    </div>
    <div v-else class="tw-right-sidebar skeleton-domain-sidebar">
      <AssessmentQuestionSubCategorySkeleton custom-class="!tw-max-w-[100%]">
      </AssessmentQuestionSubCategorySkeleton>
    </div>

    <VendorAssessmentNote
      :title="
        getComponentUiText(
          'app-vendor-create-new-vendor-assessment-modal-label-assessment-notes-1',
        )
      "
      class="tw-mt-[10px]"
      :note="noteResponse"
      @focusout="handleFocusoutUpdateNoteResponse"
      @update:value="handleUpdateNoteResponse"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType, Ref, ref, watch } from "vue";
import AssessmentCategoryHeader from "../assessment/assessmentCategoryHeader.vue";
import { formatVendorData, getComponentUiText } from "@/utils/utilities";
import ButtonBulkActions from "../common/setting/buttonBulkActions.vue";
import ButtonFilter from "../common/setting/buttonFilter.vue";
import VendorAssessmentReviewQuestionDetail from "./vendorAssessmentReviewQuestionDetail.vue";
import store from "@/store";
import AssessmentQuestionSubCategorySkeleton from "../skeleton/assessmentQuestionSubCategorySkeleton.vue";
import VendorAssessmentNote from "./vendorAssessmentNote.vue";
import { IChecklistRequested } from "@/interfaces/vendor";
import FilterModal from "../common/filter/filterModal.vue";
import CustomDropDownItem from "@/components/common/customDropDownItem.vue";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import {
  IFilterSelect,
  IFilterTarget,
  IFilterTargetValue,
  IModalMessage,
} from "@/interfaces/common/common";
import {
  ASSESSMENT_QUESTION_TYPE_FILTER_LABE,
  MODAL_NAME,
  CLR_REVIEW_OPTION,
} from "@/utils/constants";
import { useRoute } from "vue-router";
import { localStorageKey } from "@/utils/constants";
import {
  filterChecklistByName,
  groupFilters,
  putChecklistRequested,
} from "@/services/vendor";
import cloneDeep from "lodash/cloneDeep";
import VendorAssessmentSearchBox from "./vendorAssessmentSearchBox.vue";
import { isPassedSearch } from "@/services/assessment";

const props = defineProps({
  checklistData: {
    type: Object as PropType<IChecklistRequested>,
    default: () => {},
  },
  requestedIdValidSuccess: {
    type: Object as () => any,
    default: () => {},
  },
});

const route = useRoute();
const fiterTargetOption: Ref<IFilterTarget[]> = ref([]);
const assessmentId = Number(route.params.assessmentId);

const noteResponse = ref(props.checklistData.notesResponse);

watch(
  () => props.checklistData.notesResponse,
  (value) => {
    noteResponse.value = value;
  },
);

const emits = defineEmits(["close-page"]);

watch(
  () => props.requestedIdValidSuccess,
  (value) => {
    if (!store.state.Vendor.requestedDetailItem?.checklistsRequested) return [];
    const data =
      store.state.Vendor.requestedDetailItem?.checklistsRequested[0]
        ?.checklistRequestedItems ?? [];
    let dataFormat: any = [
      ...new Map(data.map((item) => [item.checklistItemId, item])).values(),
    ].filter((item: any) => !item.checklistItemIsHeading);
    if (
      dataFormat.every((item) => {
        return value[item.checklistItemId];
      })
    ) {
      emits("close-page");
    }
  },
  { deep: true },
);
const originListQuestion = computed(() => {
  if (!store.state.Vendor.requestedDetailItem?.checklistsRequested) return [];
  const data =
    store.state.Vendor.requestedDetailItem?.checklistsRequested[0]
      ?.checklistRequestedItems ?? [];
  let dataFormat = [
    ...new Map(data.map((item) => [item.checklistItemId, item])).values(),
  ];
  return dataFormat;
});

watch(originListQuestion, (value) => {
  getFilterTarget(value);
});

const listQuestion = computed(() => {
  if (!store.state.Vendor.requestedDetailItem?.checklistsRequested) return [];

  const data =
    store.state.Vendor.requestedDetailItem?.checklistsRequested[0]
      ?.checklistRequestedItems ?? [];

  let dataFormat = [
    ...new Map(data.map((item) => [item.checklistItemId, item])).values(),
  ];

  // filter by search
  const searchText = (search.value || "").trim();
  dataFormat = filterChecklistByName(dataFormat, searchText);

  // filter by filter modal
  let dataFilter: any = [];
  if (Object.keys(filterSelected.value).length) {
    dataFilter = dataFormat.filter(
      (item: any) =>
        item.checklistItemIsHeading ||
        Object.entries(filterSelected.value).every(
          ([targetId, value]) =>
            value.has(item[targetId]) ||
            (value.has(
              getComponentUiText(
                "app-assess-vendor-review-filter-target-evaluated-unreviewed-option-1",
              ),
            ) &&
              !checkQuestionIsComplete(item)),
        ),
    );
  } else {
    dataFilter = dataFormat;
  }
  const result =
    Object.keys(filterSelected.value).length || searchText.length
      ? formatVendorData(dataFilter).filter(
          (item) => item.checklistItemChildren?.length,
        )
      : formatVendorData(dataFormat);
  return result;
});

//#region Filter
const isShowFilterAction = ref(false);
const originSelected: Ref<IFilterSelect[]> = ref(
  localStorage.getItem(localStorageKey.vendorAssessmentReviewFilter)
    ? JSON.parse(
        localStorage.getItem(localStorageKey.vendorAssessmentReviewFilter) ||
          "{}",
      ) || []
    : [],
);
const filterSelected = ref(
  localStorage.getItem(localStorageKey.vendorAssessmentReviewFilter)
    ? groupFilters(
        JSON.parse(
          localStorage.getItem(localStorageKey.vendorAssessmentReviewFilter) ||
            "{}",
        ) || [],
      )
    : [],
);
const outsideFilterModal = ref(false);
const handleShowFilterAction = () => {
  if (isShowFilterAction.value) {
    outsideFilterModal.value = true;
  } else {
    isShowFilterAction.value = !isShowFilterAction.value;
  }
};

const getFilterTarget = async (listQuestion) => {
  fiterTargetOption.value = [];
  let valueEvaluateds: IFilterTargetValue[] = [],
    questionTypes: IFilterTargetValue[] = [];

  // get evaluation type
  const checklistItemComplianceTypeValueOrgIds: string[] = listQuestion.map(
    (item) => item.checklistItemComplianceTypeValueOrgId,
  );
  // deduplicate
  let uniquechecklistItemComplianceTypeValueOrgId: string[] = [
    ...new Set(checklistItemComplianceTypeValueOrgIds),
  ];
  uniquechecklistItemComplianceTypeValueOrgId =
    uniquechecklistItemComplianceTypeValueOrgId.filter((item) => item !== null);
  const assessmentQuestionType = store.state.Assessment.assessmentQuestionTypes;
  let assessmentQuestionTypesCLR: any = [];
  assessmentQuestionType.forEach((item) => {
    assessmentQuestionTypesCLR = [
      ...assessmentQuestionTypesCLR,
      ...item.complianceTypeValues,
    ];
  });
  if (uniquechecklistItemComplianceTypeValueOrgId.length) {
    valueEvaluateds = uniquechecklistItemComplianceTypeValueOrgId.map(
      (item) => {
        const data = assessmentQuestionTypesCLR?.find(
          (type) => type.id === item,
        );
        return {
          id: item || "",
          text: data?.label || "",
          isHardText: true,
        };
      },
    );
  }
  const questionNotReviewed = listQuestion.filter(
    (item) => !checkQuestionIsComplete(item),
  );
  if (questionNotReviewed && questionNotReviewed.length) {
    valueEvaluateds.push({
      id: getComponentUiText(
        "app-assess-vendor-review-filter-target-evaluated-unreviewed-option-1",
      ),
      text: getComponentUiText(
        "app-assess-vendor-review-filter-target-evaluated-unreviewed-option-1",
      ),
      isHardText: true,
    });
  }
  if (valueEvaluateds.length) {
    fiterTargetOption.value.push({
      targetId: "checklistItemComplianceTypeValueOrgId",
      targetText: "app-assess-vendor-review-filter-target-evaluated-1",
      values: valueEvaluateds,
    });
  }
  // get question type
  const assessmentQuestionTypesCL = await store.dispatch(
    "Assessment/fetchAssessmentQuestionTypes",
    "clr",
  );
  const checklistItemComplianceTypeIds: string[] = listQuestion.map(
    (item) => item.checklistItemComplianceTypeId,
  );
  // deduplicate
  let uniquechecklistItemComplianceTypeId: string[] = [
    ...new Set(checklistItemComplianceTypeIds),
  ];
  uniquechecklistItemComplianceTypeId =
    uniquechecklistItemComplianceTypeId.filter(
      (item) => item !== "na" && item !== null,
    );
  if (uniquechecklistItemComplianceTypeId.length) {
    questionTypes = uniquechecklistItemComplianceTypeId.map((item) => {
      const data = assessmentQuestionTypesCL?.find((type) => type.id === item);
      return {
        id: item,
        text: ASSESSMENT_QUESTION_TYPE_FILTER_LABE[data?.id]
          ? ASSESSMENT_QUESTION_TYPE_FILTER_LABE[data?.id]
          : data?.label,
        isHardText: ASSESSMENT_QUESTION_TYPE_FILTER_LABE[data?.id]
          ? false
          : true,
      };
    });
  }
  if (questionTypes.length) {
    fiterTargetOption.value.push({
      targetId: "checklistItemComplianceTypeId",
      targetText: "app-assess-vendor-review-filter-target-question-type-1",
      values: questionTypes,
    });
  }
};

const handleApplyFilter = (data: any) => {
  outsideFilterModal.value = false;
  isShowFilterAction.value = false;
  originSelected.value = data.filterSelecteds;
  localStorage.setItem(
    localStorageKey.vendorAssessmentReviewFilter,
    JSON.stringify(data.filterSelecteds),
  );
  filterSelected.value = groupFilters(data.filterSelecteds);
};
//#endregion Filter

//#region Bulk Actions

const selectedQuestions: any = ref([]);
const resetAllSelectedQuestions = ref(false);
const handleUpdateSelected = (items: any) => {
  items.forEach((item: any) => {
    const index = selectedQuestions.value.findIndex(
      (i) => i.checklistItemId === item.checklistItemId,
    );
    if (index > -1) {
      selectedQuestions.value.splice(index, 1);
    } else {
      selectedQuestions.value.push(item);
    }
  });
};

const isShowMoreActions = ref(false);
const handleShowBulkAction = () => {
  isShowMoreActions.value = !isShowMoreActions.value;
  if (selectedQuestions.value.length < 1) {
    isShowMoreActions.value = false;
  }
};
watch(
  () => selectedQuestions.value,
  (value) => {
    if (value.length < 1) {
      isShowMoreActions.value = false;
    }
  },
);

const showModalEditQuestion = (type: number | null) => {
  const listItems = selectedQuestions.value;

  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getComponentUiText(
        "app-component-vendor-input-bulk-action-title-1",
      ),
      description:
        type === CLR_REVIEW_OPTION.resetReview
          ? getComponentUiText(
              "app-component-vendor-input-bulk-action-sub-title-reset-1",
            )
          : getComponentUiText(
              "app-component-vendor-input-bulk-action-sub-title-1",
              false,
              {
                status: {
                  css: "tw-font-[500]",
                  value: getComponentUiText(
                    type === CLR_REVIEW_OPTION.accepted
                      ? "app-component-vendor-input-bulk-action-sub-title-accepted-1"
                      : "app-component-vendor-input-bulk-action-sub-title-not-accepted-1",
                  ),
                },
              },
            ),
      hiddenHelp: false,
      list: listItems.map(
        (item) =>
          `${item.checklistItemName}${
            item.subName ? ` | ${item.subName}` : ""
          }`,
      ),
      buttonText: getComponentUiText(
        type === CLR_REVIEW_OPTION.resetReview
          ? "app-assessment-modal-reset-answers-button-text-1"
          : "app-assessment-modal-bulk-answer-question-button-text-1",
      ),

      buttonRed: type === CLR_REVIEW_OPTION.resetReview ? true : false,
    },
    events: {
      "update:data": () => handleUpdateEvaluate(type),
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const handleUpdateEvaluate = async (type: number | null) => {
  const values = selectedQuestions.value.map((item: any) => {
    const value = {
      complianceTypeValueOrgId: type,
      checklistRequestedItemId: item.checklistItemId,
    };
    return type === CLR_REVIEW_OPTION.resetReview
      ? {
          ...value,
          complianceOverride: null,
          notesOrgInternal: "",
          notesOrgExternal: "",
        }
      : value;
  });
  await store.dispatch("Vendor/updateRequestedItems", {
    id: assessmentId,
    values,
  });
  selectedQuestions.value = [];
  resetAllSelectedQuestions.value = !resetAllSelectedQuestions.value;
};
// #endregion
const isLoading = computed(() => store.state.Vendor.isLoadingRequestedItem);
const search = ref("");

const handleUpdateNoteResponse = async (value: string) => {
  // Update value when textbox out of focus.
  if (!value || value === props.checklistData.notesResponse) return;
  noteResponse.value = value;
};

const handleFocusoutUpdateNoteResponse = async () => {
  if (
    !noteResponse.value ||
    noteResponse.value === props.checklistData.notesResponse
  )
    return;
  const res = await putChecklistRequested({
    params: {
      checklistRequestedNotesResponse: noteResponse.value,
    },
    checklistRequestedId: assessmentId,
  });
  if (!res) {
    noteResponse.value = props.checklistData.notesResponse;
  }
};

onMounted(() => {
  listenOpenImageModal();
});

const listenOpenImageModal = () => {
  EventEmitter.on(EmitterEvents.OPEN_IMAGE_MODAL, (item: any) => {
    const emitData: IModalMessage = {
      component: MODAL_NAME.MODAL_VENDOR_ASSESSMENT_ATTACHMENT,
      properties: {
        urlAttachment: item.urlAttachment,
      },
    };

    EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
  });
};

const checkQuestionIsComplete = (item: any) => {
  return (
    item.checklistItemComplianceTypeValueOrgId ||
    item.checklistItemComplianceOverride
  );
};
</script>

<style lang="scss" scoped>
:deep(.assessmentQuestion) {
  max-width: 100%;
}

.box-shadow-contain {
  box-shadow: 2px 0px 20px 0px rgba(33, 31, 88, 0.1);
}

.search-box {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  padding: 11px 15px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  max-width: 450px;
  width: 100%;
  outline: none;
  background: white;
  margin-right: 15px;
}

.search-box input {
  border: none;
  outline: none;
  width: 100%;
  margin-left: 10px;
}
.toggle-switch-view {
  border-radius: 6px;
  background: var(--ui-white, #fff);
  backdrop-filter: blur(6px);
  // padding: 4px;
  align-items: center;
  display: inline-flex;

  label {
    margin-bottom: 0;
  }
  .label {
    display: flex;
    cursor: pointer;
    padding: 10px 18px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0;
    color: var(--ui-light-grey, #b7b7b7);
    letter-spacing: 1px;
    border-radius: 6px;
    min-width: 16px;
    // Keep this coded out for now

    // min-width: 111px;
    // background: var(--brand-navy, #211F58);
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;

    &:checked + .label {
      background: var(--brand-navy, #211f58);
      color: #fff;
    }
  }
  &.setting-option-selected {
    background: var(--brand-navy, #211f58);
  }
}
</style>
